import ODataUtil from "./ODataUtil";
import { deleteItem, patchItem, postItem, ApiRequest } from './ApiUtils';
import WorkItemTracking from 'classes/WorkItemTracking';
import buildQuery from 'odata-query';

const workItemTrackingEndpoint = "/WorkItemTracking";

/**
 * Convert work item tracking API response to table schema
 */
function convertWorkItemTrackingToTableSchema({ "value": items, "@odata.count": count }) {
  // Flatten nested fields in items before passing to convertToTableRows
  const flattenedItems = items.map(item => {
    const flattened = { ...item };

    // Flatten Inspection Number and Inspector from nested expand
    if (item.Ihwi && item.Ihwi.Inspection) {
      flattened.InspectionNumber_Inspection = item.Ihwi.Inspection.InspectionNumber;

      if (item.Ihwi.Inspection.Rassignment && item.Ihwi.Inspection.Rassignment.SystemUser) {
        flattened.Name_SystemUser = item.Ihwi.Inspection.Rassignment.SystemUser.Name;
      }
    }

    // Flatten Work Item Number and Description from nested expand
    if (item.Ihwi && item.Ihwi.WorkItem) {
      flattened.WorkItemNumber_WorkItem = item.Ihwi.WorkItem.WorkItemNumber;
      flattened.Description_WorkItem = item.Ihwi.WorkItem.Description;
    }

    // Flatten Contractor Name from ContractorService -> ContractorProject -> ContractorNavigation
    if (item.ContractorService) {
      // Keep the original ServiceType description (if needed)
      if (item.ContractorService.ServiceType) {
        flattened.Description_ServiceType = item.ContractorService.ServiceType.Description;
      }

      // Extract the Contractor Name from ContractorProject
      if (item.ContractorService.ContractorProject && item.ContractorService.ContractorProject.ContractorNavigation) {
        flattened.ContractorName = item.ContractorService.ContractorProject.ContractorNavigation.Description;

        // Also flatten other contractor details for potential future use
        flattened.ContractorId = item.ContractorService.ContractorProject.ContractorNavigation.ContractorId;
        flattened.ContractorProjectId = item.ContractorService.ContractorProject.ContractorProjectId;
        flattened.ContractorComments = item.ContractorService.ContractorProject.Comments;
        flattened.ContractorMbeDesignation = item.ContractorService.ContractorProject.MbeDesignation;
        flattened.ContractorMbeLicenseNumber = item.ContractorService.ContractorProject.MbeLicenseNumber;

        // Flatten Contractor Type if available
        if (item.ContractorService.ContractorProject.ContractorType) {
          flattened.ContractorType = item.ContractorService.ContractorProject.ContractorType.Description;
        }
      }
    }

    return flattened;
  });

  const { data } = WorkItemTracking.convertToTableRows({ "value": flattenedItems, "@odata.count": count });
  return { data, count };
}

/**
 * Get work item tracking with OData query support
 */
async function getWorkItemTracking(page, per_page, orderByInfo, orderByDirection, search, filters) {
  try {
    const { skip, count, top, expand, orderBy, mappedFilter } = ODataUtil.UtilityBuilderNestedv3(
      per_page, page, WorkItemTracking.expand, orderByInfo, orderByDirection, filters
    );
    const filter = {};
    const isDefaults = filters.find(r => r.defaults);

    if (isDefaults) {
      const defaults = isDefaults.defaults;
      if(defaults.projectId) filter["ihwi/workItem/projectId"] = { eq: { type: 'guid', value: defaults.projectId }};
      if(defaults.inspectionNumber) filter["ihwi/inspection/inspectionNumber"] = defaults.inspectionNumber;
    }

    const allFilters = mappedFilter.reduce((a, b) => ({...a, ...b}), filter);
    const params = buildQuery({ count, top, skip, orderBy, expand, filter: allFilters });

    const data = await ApiRequest(workItemTrackingEndpoint, params);
    return convertWorkItemTrackingToTableSchema(data);
  } catch (error) {
    console.error('Error fetching work item tracking:', error);
    throw error;
  }
}

/**
 * Add a new work item tracking
 */
async function addWorkItemTracking(data) {
  try {
    const resourcePath = `${workItemTrackingEndpoint}/Add`;
    const result = await postItem(resourcePath, '', data);
    return result;
  } catch (error) {
    console.error('Error adding work item tracking:', error);
    throw error;
  }
}

/**
 * Update an existing work item tracking
 */
async function updateWorkItemTracking(witrackingId, data) {
  try {
    const resourcePath = `${workItemTrackingEndpoint}/Update`;
    const params = `?witrackingId=${witrackingId}`;
    const result = await patchItem(resourcePath, params, data);
    return result;
  } catch (error) {
    console.error('Error updating work item tracking:', error);
    throw error;
  }
}

/**
 * Delete a work item tracking
 */
async function deleteWorkItemTracking(witrackingId) {
  try {
    const resourcePath = `${workItemTrackingEndpoint}/Delete`;
    const params = `?witrackingId=${witrackingId}`;
    const result = await deleteItem(resourcePath, params);
    return result;
  } catch (error) {
    console.error('Error deleting work item tracking:', error);
    throw error;
  }
}

/**
 * Get a specific work item tracking by ID
 */
async function getWorkItemTrackingById(witrackingId) {
  try {
    const expand = WorkItemTracking.expand;
    const { expand: expandQuery } = ODataUtil.UtilityBuilderNestedv3(
      1,
      0,
      expand,
      null,
      null,
      []
    );

    const params = buildQuery({
      expand: expandQuery,
      filter: { WitrackingId: { eq: { type: 'guid', value: witrackingId } } }
    });

    const response = await fetch(`${workItemTrackingEndpoint}${params}`);
    const result = await response.json();

    if (result.value && result.value.length > 0) {
      return new WorkItemTracking(result.value[0]);
    }

    return null;
  } catch (error) {
    console.error('Error fetching work item tracking by ID:', error);
    throw error;
  }
}

export {
  getWorkItemTracking,
  addWorkItemTracking,
  updateWorkItemTracking,
  deleteWorkItemTracking,
  getWorkItemTrackingById
};
